/**
 * Coerces anything thrown into an Error.
 *
 * `throw` accepts any value in JavaScript, so an error boundary's `getDerivedStateFromError` is typed as
 * receiving an Error but at runtime can receive a string, null, or an object with no `message`. Storing that
 * value and then reading `.message` off it is how a fallback UI throws while rendering the failure it exists
 * to report — which unmounts the boundary and blanks the app, the exact outcome it was added to prevent.
 * Raised by Copilot on #126.
 *
 * A separate module rather than a private helper because it is pure and therefore testable without a DOM,
 * and the boundary itself is not.
 */
export const toError = (thrown: unknown): Error => {
    if (thrown instanceof Error) return thrown;

    /**
     * A plain object carrying a string `message` is worth reading rather than stringifying — rejected fetch
     * promises and third-party libraries both produce them, and `String(value)` would turn one into the
     * useless "[object Object]".
     */
    if (typeof thrown === 'object' && thrown !== null && 'message' in thrown) {
        const {message} = thrown as {message: unknown};

        if (typeof message === 'string' && message.length > 0) return new Error(message);
    }

    // String(null) is "null" and String(undefined) is "undefined", which are unhelpful but honest — and
    // still better than a fallback that cannot render.
    return new Error(typeof thrown === 'string' && thrown.length > 0 ? thrown : String(thrown));
};
